import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  Box,
  Divider,
  Chip,
  Avatar,
} from '@mui/material';
import {
  LocalHospital,
  EventAvailable,
  MedicalServices,
  Science,
  Phone,
  Email,
  LocationOn,
  Favorite,
  ChildCare,
  Visibility,
  Hearing,
  Psychology,
  AccessibilityNew,
  PregnantWoman,
  Biotech,
  EmergencyShare,
  VerifiedUser,
  Groups,
  Schedule,
  Star,
  ChevronLeft,
  ChevronRight,
  OpenInNew,
  CampaignOutlined,
} from '@mui/icons-material';
import { mediaService, homePageSettingsService } from '../services/api';

const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

// ─── Icon registry — admin picks one of these keys per content item ─────────
const ICON_COMPONENTS = {
  LocalHospital, EventAvailable, MedicalServices, Science, Favorite,
  ChildCare, Visibility, Hearing, Psychology, AccessibilityNew,
  PregnantWoman, Biotech, EmergencyShare, VerifiedUser, Groups,
  Schedule, Star,
};
const renderIcon = (name, sx) => {
  const Cmp = ICON_COMPONENTS[name] || MedicalServices;
  return <Cmp sx={sx} />;
};

// ─── Fallback content — used until /api/home-settings resolves, or if a customer hasn't configured a block yet ──
const DEFAULT_SETTINGS = {
  siteMode: 'Hospital',
  brandName: 'Dr. Dhawal Sawant',
  tagline: "Safe motherhood and expert care for women's on every stage",
  primaryColor: '#0d47a1',
  establishedYear: '2018',
  heroHeadline: 'Your Health,\nOur Commitment',
  heroSubtext: 'Compassionate, evidence-based care delivered by a team of 8+ specialists — all under one roof.',
  aboutText: 'Serving the community with excellence since 2018.',
  phone1: '+91 92098 98331',
  phone2: '+91 72188 80329',
  email: 'sawantgyn@gmail.com',
  address: 'Near Hotel Grand Sita, Beside Harinkhede Petrol Pump, Tiroda Road, Gondia (M.H)',
  workingHours: 'Open 24 Hours  |  Emergency: 24/7',
  footerTagline: 'Delivering compassionate, world-class healthcare to our community since 2010. Your health and well-being remain our highest priority.',
  showDoctorsSection: true,
  showDemoBanner: false,
  demoBannerText: '',
};

const DEFAULT_STATS = [
  { value: '8+', label: 'Years of Excellence' },
  { value: '20,000+', label: 'Happy Patients' },
  { value: '8+', label: 'Specialist Doctors' },
  { value: '24/7', label: 'Emergency & ICU Care' },
];

const DEFAULT_SPECIALITIES = [
  { title: 'Obstetrics & Gynaecology', icon: 'PregnantWoman', color: '#e91e63' },
  { title: 'Cardiology', icon: 'Favorite', color: '#f44336' },
  { title: 'Paediatrics', icon: 'ChildCare', color: '#ff9800' },
  { title: 'Neurology', icon: 'Psychology', color: '#9c27b0' },
  { title: 'Orthopaedics', icon: 'AccessibilityNew', color: '#2196f3' },
  { title: 'Ophthalmology', icon: 'Visibility', color: '#00bcd4' },
  { title: 'ENT', icon: 'Hearing', color: '#4caf50' },
  { title: 'Pathology & Lab', icon: 'Biotech', color: '#795548' },
  { title: 'General Medicine', icon: 'MedicalServices', color: '#607d8b' },
  { title: 'Dermatology', icon: 'Science', color: '#ff5722' },
];

const DEFAULT_SERVICES = [
  { icon: 'LocalHospital', title: 'OPD Services', description: 'Walk-in consultations with experienced specialists across all departments, Mon – Sat.', color: '#1976d2' },
  { icon: 'EmergencyShare', title: '24 / 7 Emergency', description: 'Round-the-clock emergency care with fully equipped trauma and critical care units.', color: '#d32f2f' },
  { icon: 'Science', title: 'Diagnostic Lab', description: 'NABL-accredited laboratory delivering precise results for 500+ tests within hours.', color: '#00897b' },
  { icon: 'EventAvailable', title: 'Easy Appointments', description: 'Schedule consultations quickly through our streamlined in-clinic booking system.', color: '#7b1fa2' },
  { icon: 'PregnantWoman', title: 'Maternity Care', description: 'Comprehensive antenatal, delivery, and postnatal care for mother and baby.', color: '#e91e63' },
  { icon: 'VerifiedUser', title: 'Health Packages', description: 'Preventive health check-up packages tailored for individuals, couples and corporates.', color: '#f57c00' },
];

const DEFAULT_WHY_US = [
  { icon: 'Groups', title: '8+ Specialists', desc: 'Qualified doctors across every major discipline.' },
  { icon: 'Biotech', title: 'Modern Equipment', desc: 'Latest diagnostic and surgical technology.' },
  { icon: 'Schedule', title: 'Minimal Wait Times', desc: 'Efficient scheduling keeps your time valued.' },
  { icon: 'Star', title: 'Patient-First Care', desc: 'Compassionate, personalised treatment every visit.' },
];

const safeParse = (json, fallback) => {
  if (!json) return fallback;
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const HomePage = () => {
  const navigate = useNavigate();
  const [mediaItems, setMediaItems] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showDiscretion, setShowDiscretion] = useState(false);
  const [discretionCountdown, setDiscretionCountdown] = useState(5);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [specialities, setSpecialities] = useState(DEFAULT_SPECIALITIES);
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [whyUs, setWhyUs] = useState(DEFAULT_WHY_US);
  const [doctors, setDoctors] = useState([]);

  const PRIMARY = settings.primaryColor || DEFAULT_SETTINGS.primaryColor;

  useEffect(() => {
    loadMedia();
    loadHomeSettings();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await mediaService.getAllMedia();
      setMediaItems(response.data);
    } catch (error) {
      console.error('Error loading media:', error);
    }
  };

  const loadHomeSettings = async () => {
    try {
      const response = await homePageSettingsService.getSettings();
      const d = response.data;
      setSettings({
        siteMode: d.siteMode || DEFAULT_SETTINGS.siteMode,
        brandName: d.brandName || DEFAULT_SETTINGS.brandName,
        tagline: d.tagline || DEFAULT_SETTINGS.tagline,
        primaryColor: d.primaryColor || DEFAULT_SETTINGS.primaryColor,
        establishedYear: d.establishedYear || DEFAULT_SETTINGS.establishedYear,
        heroHeadline: d.heroHeadline || DEFAULT_SETTINGS.heroHeadline,
        heroSubtext: d.heroSubtext || DEFAULT_SETTINGS.heroSubtext,
        aboutText: d.aboutText || DEFAULT_SETTINGS.aboutText,
        phone1: d.phone1 || DEFAULT_SETTINGS.phone1,
        phone2: d.phone2 || '',
        email: d.email || DEFAULT_SETTINGS.email,
        address: d.address || DEFAULT_SETTINGS.address,
        workingHours: d.workingHours || DEFAULT_SETTINGS.workingHours,
        footerTagline: d.footerTagline || DEFAULT_SETTINGS.footerTagline,
        showDoctorsSection: d.showDoctorsSection ?? DEFAULT_SETTINGS.showDoctorsSection,
        showDemoBanner: d.showDemoBanner ?? DEFAULT_SETTINGS.showDemoBanner,
        demoBannerText: d.demoBannerText || 'This is a demo site showcasing the Hospital Management System.',
      });
      setStats(safeParse(d.statsJson, DEFAULT_STATS));
      setSpecialities(safeParse(d.specialitiesJson, DEFAULT_SPECIALITIES));
      setServices(safeParse(d.servicesJson, DEFAULT_SERVICES));
      setWhyUs(safeParse(d.whyUsJson, DEFAULT_WHY_US));

      if (d.showDoctorsSection) {
        try {
          const docRes = await homePageSettingsService.getDoctors();
          setDoctors(docRes.data || []);
        } catch (err) {
          console.error('Error loading doctors:', err);
        }
      }
    } catch (error) {
      console.error('Error loading home page settings, using defaults:', error);
    }
  };

  // When slide changes, check if it needs viewer discretion overlay
  const goToSlide = useCallback((index, items) => {
    const list = items || mediaItems;
    const target = list[index];
    setCurrentSlide(index);
    if (target?.viewerDiscretion) {
      setShowDiscretion(true);
      setDiscretionCountdown(5);
    } else {
      setShowDiscretion(false);
    }
  }, [mediaItems]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % mediaItems.length);
  }, [currentSlide, mediaItems.length, goToSlide]);

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + mediaItems.length) % mediaItems.length);
  };

  // Countdown timer for viewer discretion overlay
  useEffect(() => {
    if (!showDiscretion) return;
    if (discretionCountdown <= 0) {
      setShowDiscretion(false);
      return;
    }
    const t = setTimeout(() => setDiscretionCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showDiscretion, discretionCountdown]);

  // Pause auto-advance while discretion overlay is showing
  useEffect(() => {
    if (mediaItems.length <= 1 || isPaused || showDiscretion) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [mediaItems.length, isPaused, nextSlide, showDiscretion]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ fontFamily: 'Poppins, sans-serif', bgcolor: '#fff' }}>

      {/* ── Demo banner ── */}
      {settings.showDemoBanner && (
        <Box sx={{ bgcolor: '#ed6c02', color: '#fff', py: 0.8, textAlign: 'center' }}>
          <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CampaignOutlined sx={{ fontSize: 18 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{settings.demoBannerText}</Typography>
          </Container>
        </Box>
      )}

      {/* ── Top contact bar ── */}
      <Box sx={{ bgcolor: PRIMARY, color: '#fff', py: 0.6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: 14 }} />
                <Typography variant="caption">{[settings.phone1, settings.phone2].filter(Boolean).join(' / ')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 14 }} />
                <Typography variant="caption">{settings.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 14 }} />
                <Typography variant="caption">{settings.workingHours}</Typography>
              </Box>
            </Box>
            <Chip label="24/7 Emergency" size="small" sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
          </Box>
        </Container>
      </Box>

      {/* ── Navbar ── */}
      <AppBar position="sticky" elevation={2} sx={{ bgcolor: '#fff', color: PRIMARY }}>
        <Toolbar sx={{ py: 0.5 }}>
          <LocalHospital sx={{ fontSize: 36, mr: 1.5, color: PRIMARY }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Montserrat', color: PRIMARY, lineHeight: 1.2, fontSize: { xs: '0.95rem', md: '1.15rem' } }}>
              {settings.brandName}
            </Typography>
            <Typography variant="caption" sx={{ color: PRIMARY, letterSpacing: 1, fontSize: '0.65rem', display: 'block' }}>
              {settings.tagline}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{
              bgcolor: PRIMARY,
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:hover': { bgcolor: PRIMARY, opacity: 0.9 },
            }}
          >
            Staff Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* ── Hero ── */}
      <Box id="home" sx={{
        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY}cc 50%, ${PRIMARY}99 100%)`,
        color: '#fff',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* decorative circles */}
        <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', top: -100, right: -100 }} />
        <Box sx={{ position: 'absolute', width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', bottom: -50, left: -50 }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label={settings.brandName} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, mb: 2, letterSpacing: 0.5 }} />
              <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontFamily: 'Montserrat', lineHeight: 1.2, mb: 2, fontSize: { xs: '2rem', md: '3rem' }, whiteSpace: 'pre-line' }}>
                {settings.heroHeadline}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.88, mb: 1.5, fontWeight: 400, lineHeight: 1.7 }}>
                {settings.heroSubtext}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.75, mb: 4 }}>
                {settings.aboutText}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ bgcolor: '#fff', color: PRIMARY, fontWeight: 700, px: 4, py: 1.5, borderRadius: 2, '&:hover': { bgcolor: '#e3f2fd' } }}
                >
                  Staff Portal
                </Button>
                {settings.phone1 && (
                  <Button
                    variant="outlined"
                    size="large"
                    href={`tel:${settings.phone1.replace(/\s+/g, '')}`}
                    sx={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff', fontWeight: 600, px: 4, py: 1.5, borderRadius: 2, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                  >
                    Call Us
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Stats cards */}
              <Grid container spacing={2}>
                {stats.map((stat, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 3,
                      p: 3,
                      textAlign: 'center',
                    }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '2rem', fontFamily: 'Montserrat', lineHeight: 1 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85, mt: 0.5, display: 'block', letterSpacing: 0.3 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Specialities ── */}
      {specialities.length > 0 && (
        <Box id="specialities" sx={{ bgcolor: '#f8fafc', py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 2 }}>
                What We Treat
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
                Our Specialities
              </Typography>
              <Divider sx={{ width: 60, height: 3, bgcolor: PRIMARY, mx: 'auto', mt: 2, border: 'none' }} />
            </Box>
            <Grid container spacing={2.5}>
              {specialities.map((sp, i) => (
                <Grid item xs={6} sm={4} md={2.4} key={i}>
                  <Card elevation={0} sx={{
                    textAlign: 'center',
                    p: 2.5,
                    border: '1px solid #e8eef4',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    '&:hover': { boxShadow: `0 8px 24px ${sp.color}1f`, borderColor: sp.color, transform: 'translateY(-4px)' },
                  }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: '50%',
                      bgcolor: `${sp.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 1.5, color: sp.color,
                    }}>
                      {renderIcon(sp.icon, { fontSize: 36 })}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}>
                      {sp.title}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ── Our Doctors (Hospital mode) ── */}
      {settings.showDoctorsSection && doctors.length > 0 && (
        <Box id="doctors" sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 2 }}>
                Meet The Team
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
                Our Doctors
              </Typography>
              <Divider sx={{ width: 60, height: 3, bgcolor: PRIMARY, mx: 'auto', mt: 2, border: 'none' }} />
            </Box>
            <Grid container spacing={3}>
              {doctors.map((doc, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card elevation={0} sx={{ textAlign: 'center', p: 3, height: '100%', border: '1px solid #e8eef4', borderRadius: 3, transition: 'all 0.3s', '&:hover': { boxShadow: `0 8px 24px ${PRIMARY}22`, borderColor: PRIMARY } }}>
                    <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, bgcolor: `${PRIMARY}15`, color: PRIMARY, fontWeight: 700, fontSize: '1.5rem' }}>
                      {(doc.firstName || doc.FirstName || '?').charAt(0)}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Dr. {doc.firstName || doc.FirstName} {doc.lastName || doc.LastName}
                    </Typography>
                    {(doc.specialization || doc.Specialization) && (
                      <Chip label={doc.specialization || doc.Specialization} size="small" sx={{ mt: 1, bgcolor: `${PRIMARY}15`, color: PRIMARY, fontWeight: 600 }} />
                    )}
                    {(doc.education || doc.Education) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {doc.education || doc.Education}
                      </Typography>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ── Services ── */}
      {services.length > 0 && (
        <Box id="services" sx={{ py: 8, bgcolor: settings.showDoctorsSection && doctors.length > 0 ? '#f8fafc' : 'transparent' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 2 }}>
                What We Offer
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
                Our Services
              </Typography>
              <Divider sx={{ width: 60, height: 3, bgcolor: PRIMARY, mx: 'auto', mt: 2, border: 'none' }} />
            </Box>
            <Grid container spacing={3}>
              {services.map((service, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card elevation={0} sx={{
                    p: 3, height: '100%',
                    border: '1px solid #e8eef4',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': { boxShadow: `0 12px 32px ${service.color}22`, borderColor: service.color, transform: 'translateY(-4px)' },
                  }}>
                    <Box sx={{
                      width: 72, height: 72, borderRadius: 2.5,
                      bgcolor: `${service.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 2.5, color: service.color,
                    }}>
                      {renderIcon(service.icon, { fontSize: 48 })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {service.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ── Why Choose Us ── */}
      {whyUs.length > 0 && (
        <Box sx={{ bgcolor: PRIMARY, color: '#fff', py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: '#90caf9', fontWeight: 700, letterSpacing: 2 }}>
                Why Patients Trust Us
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
                Why Choose Us?
              </Typography>
              <Divider sx={{ width: 60, height: 3, bgcolor: '#90caf9', mx: 'auto', mt: 2, border: 'none' }} />
            </Box>
            <Grid container spacing={4}>
              {whyUs.map((item, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: 80, height: 80, borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 2,
                    }}>
                      {renderIcon(item.icon, { fontSize: 40, color: '#90caf9' })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{item.title}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.7 }}>{item.desc}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ── Media Carousel ── */}
      {mediaItems.length > 0 && (
        <Box id="gallery" sx={{ bgcolor: '#f8fafc', py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 2 }}>
                Gallery
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
                Healthcare at your Doorstep
              </Typography>
              <Divider sx={{ width: 60, height: 3, bgcolor: PRIMARY, mx: 'auto', mt: 2, border: 'none' }} />
            </Box>

            {/* Carousel */}
            <Box
              sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.15)' }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Slides */}
              <Box sx={{ position: 'relative', width: '100%', height: { xs: 260, sm: 400, md: 520 }, overflow: 'hidden', bgcolor: '#f8fafc' }}>
                {mediaItems.map((item, idx) => (
                  <Box
                    key={item.id}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      transition: 'opacity 0.7s ease, transform 0.7s ease',
                      opacity: idx === currentSlide ? 1 : 0,
                      transform: idx === currentSlide ? 'translateX(0)' : idx < currentSlide ? 'translateX(-60px)' : 'translateX(60px)',
                      pointerEvents: idx === currentSlide ? 'auto' : 'none',
                    }}
                  >
                    <Box
                      component="img"
                      src={item.url?.startsWith('http') ? item.url : `${MEDIA_BASE_URL}${item.url}`}
                      alt={item.title || item.fileName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        bgcolor: '#f8fafc',
                      }}
                    />
                    {/* Caption overlay */}
                    {(item.title || item.description || item.hyperlink) && (
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                        px: 4, py: 3, color: '#fff',
                        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2,
                      }}>
                        <Box>
                          {item.title && (
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                              {item.title}
                            </Typography>
                          )}
                          {item.description && (
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                        {item.hyperlink && (
                          <Box
                            component="a"
                            href={item.hyperlink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View more"
                            sx={{
                              flexShrink: 0,
                              width: 44, height: 44,
                              borderRadius: '50%',
                              bgcolor: 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(6px)',
                              border: '1.5px solid rgba(255,255,255,0.5)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff',
                              textDecoration: 'none',
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: PRIMARY, borderColor: PRIMARY, transform: 'scale(1.1)' },
                            }}
                          >
                            <OpenInNew sx={{ fontSize: 20 }} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Viewer Discretion Overlay */}
              {showDiscretion && (
                <Box sx={{
                  position: 'absolute', inset: 0, zIndex: 20,
                  bgcolor: 'rgba(0,0,0,0.85)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 2,
                }}>
                  <Box sx={{ fontSize: 56 }}>⚠️</Box>
                  <Typography variant="h4" sx={{ color: '#ffcc00', fontWeight: 800, textAlign: 'center', px: 3 }}>
                    Viewer Discretion Advised
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', px: 4 }}>
                    This image may contain sensitive content.
                  </Typography>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '50%',
                    border: '4px solid #ffcc00',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mt: 1,
                  }}>
                    <Typography variant="h4" sx={{ color: '#ffcc00', fontWeight: 800 }}>
                      {discretionCountdown}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Image will display automatically
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={nextSlide}
                    sx={{ mt: 1, color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}
                  >
                    Skip — Next Image
                  </Button>
                </Box>
              )}

              {/* Prev / Next arrows */}
              {mediaItems.length > 1 && (
                <>
                  <Box
                    onClick={prevSlide}
                    sx={{
                      position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '50%',
                      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': { bgcolor: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <ChevronLeft sx={{ color: PRIMARY, fontSize: 28 }} />
                  </Box>
                  <Box
                    onClick={nextSlide}
                    sx={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '50%',
                      width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': { bgcolor: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <ChevronRight sx={{ color: PRIMARY, fontSize: 28 }} />
                  </Box>
                </>
              )}

              {/* Dot indicators */}
              {mediaItems.length > 1 && (
                <Box sx={{
                  position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                  display: 'flex', gap: 1, zIndex: 10,
                }}>
                  {mediaItems.map((_, idx) => (
                    <Box
                      key={idx}
                      onClick={() => goToSlide(idx)}
                      sx={{
                        width: idx === currentSlide ? 24 : 8,
                        height: 8, borderRadius: 4,
                        bgcolor: idx === currentSlide ? '#fff' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Thumbnail strip */}
            {mediaItems.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 2, overflowX: 'auto', pb: 1, justifyContent: mediaItems.length <= 6 ? 'center' : 'flex-start' }}>
                {mediaItems.map((item, idx) => (
                  <Box
                    key={item.id}
                    onClick={() => goToSlide(idx)}
                    sx={{
                      flexShrink: 0,
                      width: 80, height: 56, borderRadius: 2,
                      overflow: 'hidden', cursor: 'pointer',
                      border: idx === currentSlide ? `3px solid ${PRIMARY}` : '3px solid transparent',
                      opacity: idx === currentSlide ? 1 : 0.6,
                      transition: 'all 0.2s',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.url?.startsWith('http') ? item.url : `${MEDIA_BASE_URL}${item.url}`}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: '#f8fafc' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* ── Contact ── */}
      <Box id="contact" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 2 }}>
              Reach Us
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Montserrat', mt: 0.5 }}>
              Contact Us
            </Typography>
            <Divider sx={{ width: 60, height: 3, bgcolor: PRIMARY, mx: 'auto', mt: 2, border: 'none' }} />
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {[
              { icon: <Phone sx={{ fontSize: 36, color: PRIMARY }} />, title: 'Phone', lines: [settings.phone1, settings.phone2].filter(Boolean), color: PRIMARY },
              { icon: <Email sx={{ fontSize: 36, color: '#00897b' }} />, title: 'Email', lines: [settings.email].filter(Boolean), color: '#00897b' },
              { icon: <LocationOn sx={{ fontSize: 36, color: '#7b1fa2' }} />, title: 'Location', lines: [settings.address].filter(Boolean), color: '#7b1fa2' },
              { icon: <Schedule sx={{ fontSize: 36, color: '#f57c00' }} />, title: 'Working Hours', lines: [settings.workingHours].filter(Boolean), color: '#f57c00' },
            ].map((c, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card elevation={0} sx={{ textAlign: 'center', p: 3, border: '1px solid #e8eef4', borderRadius: 3, height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: `0 8px 24px ${c.color}22`, borderColor: c.color } }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: `${c.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    {c.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{c.title}</Typography>
                  {c.lines.map((l, j) => (
                    <Typography key={j} variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{l}</Typography>
                  ))}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: '#0a1929', color: '#fff', pt: 6, pb: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Brand */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LocalHospital sx={{ fontSize: 28, color: '#90caf9' }} />
                <Typography sx={{ fontWeight: 800, fontFamily: 'Montserrat', fontSize: '1rem', color: '#fff' }}>
                  {settings.brandName}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#90a4ae', lineHeight: 1.8, mb: 2 }}>
                {settings.footerTagline}
              </Typography>
              <Chip label={settings.tagline} size="small" sx={{ bgcolor: '#1565c0', color: '#90caf9', fontWeight: 700, letterSpacing: 1, fontSize: '0.65rem' }} />
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
                Quick Links
              </Typography>
              {[
                { label: 'Home', id: 'home' },
                { label: 'Services', id: 'services' },
                { label: 'Specialities', id: 'specialities' },
                { label: 'Gallery', id: 'gallery' },
                { label: 'Contact', id: 'contact' },
              ].map((link) => (
                <Typography
                  key={link.id}
                  variant="body2"
                  onClick={() => scrollToSection(link.id)}
                  sx={{ color: '#90a4ae', mb: 0.8, cursor: 'pointer', '&:hover': { color: '#fff' }, transition: 'color 0.2s' }}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>

            {/* Specialities */}
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
                Specialities
              </Typography>
              {specialities.slice(0, 5).map((s) => (
                <Typography key={s.title} variant="body2" sx={{ color: '#90a4ae', mb: 0.8 }}>
                  {s.title}
                </Typography>
              ))}
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
                Contact
              </Typography>
              {[
                { icon: <Phone sx={{ fontSize: 14 }} />, text: [settings.phone1, settings.phone2].filter(Boolean).join(' / ') },
                { icon: <Email sx={{ fontSize: 14 }} />, text: settings.email },
                { icon: <LocationOn sx={{ fontSize: 14 }} />, text: settings.address },
              ].filter((c) => c.text).map((c, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Box sx={{ color: '#90caf9', mt: 0.2, flexShrink: 0 }}>{c.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#90a4ae', lineHeight: 1.6 }}>{c.text}</Typography>
                </Box>
              ))}
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#546e7a' }}>
              © {new Date().getFullYear()} {settings.brandName}. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ color: '#546e7a' }}>
              Privacy Policy &nbsp;·&nbsp; Terms of Service
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 1.5 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#90caf9' }}>
              Designed &amp; maintained by{' '}
              <Box
                component="a"
                href="https://q-erasoftware.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#90caf9', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                Q-Era Software Pvt. Ltd.
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
